import { datadogLogs } from '@datadog/browser-logs';
import type { ApiPermissionGrantV3 } from '@/types/permissionGrants.v3';
import { PERMISSION_GRANT_KEYS, type GrantKey, type PermissionDraftMap } from '@/types/permissionModules';

const KNOWN_GRANT_KEYS: ReadonlySet<string> = new Set(PERMISSION_GRANT_KEYS);

/**
 * Roster VIEW is derived, never stored. BE sends no row for it, but
 * if one ever arrives it must not enter the draft or `hasChanges` would read dirty.
 */
const DERIVED_GRANT_KEYS: ReadonlySet<string> = new Set<GrantKey>(['viewRoster']);

/**
 * Maps saved grants to the drawer's sparse draft. `team_twrn` is ignored: at GA every
 * grant is org-wide. Permissions this build does not know are returned in `unknown`
 * so the caller can report them. They stay out of the draft.
 */
export function grantsToDraftMap(grants: ApiPermissionGrantV3[]): {
  draft: PermissionDraftMap;
  unknown: string[];
} {
  const draft: PermissionDraftMap = {};
  const unknown = new Set<string>();

  grants.forEach(({ permission }) => {
    if (DERIVED_GRANT_KEYS.has(permission)) return;

    if (KNOWN_GRANT_KEYS.has(permission)) {
      draft[permission as GrantKey] = true;
    } else {
      unknown.add(permission);
    }
  });

  return {
    draft,
    unknown: [...unknown],
  };
}

/**
 * Builds the wire payload from the drawer's sparse draft. Only granted keys are emitted, so an
 * empty draft is a valid revoke-all payload. Every entry is un-narrowed (`team_twrn: null`) —
 * at GA the drawer never narrows a grant to a team.
 */
export function draftMapToGrantsV3(draft: PermissionDraftMap): ApiPermissionGrantV3[] {
  return (Object.keys(draft) as GrantKey[])
    .filter((key) => draft[key])
    .map((permission) => ({
      permission,
      team_twrn: null,
    }));
}

/** Console in dev (Datadog is not initialised there), Datadog everywhere else. */
export function reportUnknownGrants(userTwrn: string, permissions: string[]) {
  if (!permissions.length) return;

  if (import.meta.env.DEV) {
    console.warn(`[permissions] Unknown permission grants for ${userTwrn}: ${permissions.join(', ')}`);
    return;
  }

  datadogLogs.logger.warn('permissions.unknown_grants', {
    userTwrn,
    permissions,
  });
}
